import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import axios from '../api/axios';
import styles from './LeaderDashboard.module.css';
import rawMetadata from '../config/user-metadata.json';
import { useAuth } from '../context/AuthContext';
import api from '../api/axios';
import { getLevel } from '../utils/levels';

const UserManagement: React.FC = () => {
  const { user: currentUser, refreshUser } = useAuth();

  interface ActivityLog {
    id: number;
    timestamp: string;
    action: string;
  }

  interface OrgJourneyData {
    interests: string[];
    playstyle: string;
    squadPreference: string;
    skills: string;
    primaryRole?: string;
    secondaryRole?: string;
    tertiaryRole?: string;
  }

  interface PointTransaction {
    id: number;
    points: number;
    reason: string;
    createdAt: string;
    createdBy?: string;
    missionTitle?: string;
  }

  interface PositionMeta {
    title: string;
    id: number;
    department: string;
    scopes: string[];
    rank?: string;
    tier?: number;
  }

  interface UserMetadata {
    roles: string[];
    flags: string[];
    positions: PositionMeta[];
    positionMap: { [key: string]: number };
    departments: string[];
    scopes: string[];
  }

  interface User {
    id: number;
    username: string;
    email: string;
    role: string;
    position: string | null;
    primaryPositionTitle?: string;
    secondaryPositionTitle?: string;
    tertiaryPositionTitle?: string;
    primaryRank?: string;
    secondaryRank?: string;
    tertiaryRank?: string;
    flags: string[];
    gameUserName: string;
    discordName: string;
    joinDate: string;
    lastLogin?: string;
    createdAt: string;
    activityScore: number;
    isOnline: boolean;
    mainInGameRole: string;
    secondaryInGameRoles: string | null;
    activityLogs: ActivityLog[];
    avatarUrl?: string;
    orgJourneyData?: OrgJourneyData;
    profileTheme?: string;
    commendationsCount?: number;
  }

  type EditUser = Partial<Omit<User, 'flags' | 'position' | 'email'>> & {
    flags?: string[];
    primaryPosition?: string;
    primaryRank?: string;
    secondaryPosition?: string;
    secondaryRank?: string;
    tertiaryPosition?: string;
    tertiaryRank?: string;
  };

  const userMetadata = rawMetadata as UserMetadata;
  const [users, setUsers] = useState<User[]>([]);
  const [positions, setPositions] = useState<PositionMeta[]>([]);
  const [viewMode, setViewMode] = useState<'table' | 'card'>('table');
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [editState, setEditState] = useState<EditUser>({});
  const [pointsAdjustment, setPointsAdjustment] = useState<number>(0);
  const [pointsReason, setPointsReason] = useState<string>('');
  const [userPointsBalance, setUserPointsBalance] = useState<number>(0);
  const [pointsHistory, setPointsHistory] = useState<PointTransaction[]>([]);
  const [showPointsHistory, setShowPointsHistory] = useState<boolean>(false);
  const [userPoints, setUserPoints] = useState<Map<number, number>>(new Map());

  // Filter states
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [filterRole, setFilterRole] = useState<string>('all');
  const [filterDepartment, setFilterDepartment] = useState<string>('all');
  const [filterOnlineStatus, setFilterOnlineStatus] = useState<string>('all');
  const [filterPosition, setFilterPosition] = useState<string>('all');
  const [filterFlag, setFilterFlag] = useState<string>('all');
  const [filterLevel, setFilterLevel] = useState<string>('all');

  useEffect(() => {
    refreshUser();
    fetchUsers();
    fetchPositions();
    fetchAllUserPoints();
  }, []);

  // 🔒 Authorization: Admin + CEO + COO + CHO only
  const isCeoOrCooOrCho = (): boolean => {
    if (!currentUser) return false;
    const titles = [
      currentUser.primaryPositionTitle,
      currentUser.secondaryPositionTitle,
      currentUser.tertiaryPositionTitle
    ].filter(Boolean).map((t: string) => t.toLowerCase());
    
    return titles.includes('ceo') || 
           titles.includes('chief executive officer') || 
           titles.includes('coo') || 
           titles.includes('chief operating officer') ||
           titles.includes('cho') ||
           titles.includes('chief human resource officer');
  };

  const hasAccess = currentUser?.role === 'Admin' || isCeoOrCooOrCho();

  if (!currentUser) {
    return <Navigate to="/login" replace />;
  }

  if (!hasAccess) {
    return <Navigate to="/home" replace />;
  }

  const fetchAllUserPoints = async () => {
    try {
      const pointsMap = new Map<number, number>();
      for (const user of users) {
        try {
          const res = await api.get(`/auth/user/${user.id}/points/balance`);
          pointsMap.set(user.id, res.data.totalPoints || 0);
        } catch (err) {
          console.error(`Failed to fetch points for user ${user.id}:`, err);
          pointsMap.set(user.id, 0);
        }
      }
      setUserPoints(pointsMap);
    } catch (err) {
      console.error('Failed to fetch user points:', err);
    }
  };

  const fetchPositions = async () => {
    const res = await api.get('/auth/user/positions');
    setPositions(res.data);
  };

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get('/auth/user/directory', {
        headers: { Authorization: `Bearer ${token}` },
      });
      const loadedUsers = res.data.map((user: any) => ({
        ...user,
        position: user.primaryPositionTitle ?? null,
        avatarUrl: user.avatarUrl ?? null,
        isOnline: Boolean(user.isOnline),
        orgJourneyData: user.orgJourneyData ?? null,
        flags: user.flags ?? [],
        primaryRank: user.orgJourneyData?.primaryRole ?? user.primaryRank ?? null,
        secondaryRank: user.orgJourneyData?.secondaryRole ?? user.secondaryRank ?? null,
        tertiaryRank: user.orgJourneyData?.tertiaryRole ?? user.tertiaryRank ?? null,
        profileTheme: user.profileTheme ?? null,
        commendationsCount: user.commendationsCount ?? 0,
      }));
      setUsers(loadedUsers);
      
      // Fetch points for all users
      if (currentUser?.role === 'Admin') {
        const pointsMap = new Map<number, number>();
        for (const user of loadedUsers) {
          try {
            const pointsRes = await api.get(`/auth/user/${user.id}/points/balance`);
            pointsMap.set(user.id, pointsRes.data.totalPoints || 0);
          } catch (err) {
            pointsMap.set(user.id, 0);
          }
        }
        setUserPoints(pointsMap);
      }
    } catch (err) {
      console.error('Failed to fetch users:', err);
    }
  };

  const formatFlag = (flag: string) => {
    switch (flag) {
      case 'CanManageUsers':
        return 'Can Manage Users';
      case 'CanEditEvents':
        return 'Can Edit Events';
      case 'canCreatePosts':
        return 'Can Create Posts';
      case 'CanManagePosts':
        return 'Can Manage Posts';
      case 'OrgMember':
        return 'Org Member';
      case 'AccessMedicalReports':
        return 'Access Medical Reports';
      case 'viewAllDepartments':
        return 'View All Departments';
      case 'CanAcceptMissions':
        return 'Can Accept Missions';
      default:
        return flag;
    }
  };

  const getFullAvatarUrl = (avatarUrl: string | null | undefined): string | null => {
    if (!avatarUrl) return null;
    if (avatarUrl.startsWith('http')) return avatarUrl;
    return `${process.env.REACT_APP_API_URL}${avatarUrl}`;
  };

  const getAvatarBorderColor = (theme?: string): string => {
    switch (theme) {
      case 'elite-blue': return '#3b82f6';
      case 'master-gold': return '#f59e0b';
      case 'legend-purple': return '#a855f7';
      default: return 'var(--border-color)';
    }
  };

  const getUserScopes = (): string[] => {
    if (!currentUser) return [];

    let roleScopes: string[] = [];

    if (currentUser.role === 'Admin') {
      roleScopes = userMetadata.scopes;
    } else if (currentUser.role === 'Leader') {
      roleScopes = ['createPost', 'createEvent'];
    }

    const positionScopes: string[] =
      userMetadata.positions.find((p) => p.title === currentUser.position)?.scopes || [];

    const flagScopes: string[] = (currentUser.flags || [])
      .map((flag: string) => {
        switch (flag.toLowerCase()) {
          case 'canmanageusers':
            return 'manageMembers';
          case 'caneditevents':
            return 'createEvent';
          case 'cancreateposts':
            return 'createPost';
          case 'canmanageposts':
            return 'managePosts';
          default:
            return '';
        }
      })
      .filter(Boolean);

    return Array.from(new Set([...roleScopes, ...positionScopes, ...flagScopes]));
  };

  const userScopes = getUserScopes();

  // Check if current user can edit the target user
  const canEditUser = (targetUser: User): boolean => {
    // Admins can edit anyone
    if (currentUser?.role === 'Admin') return true;
    
    // Leaders CANNOT edit Admins or other Leaders at all
    if (currentUser?.role === 'Leader' && (targetUser.role === 'Leader' || targetUser.role === 'Admin')) {
      return false;
    }
    
    // Otherwise, if they have manageMembers scope, they can edit
    return userScopes.includes('manageMembers');
  };

  const handleUserClick = (user: User) => {
    setSelectedUser(user);
    setEditState({
      role: user.role,
      primaryPosition: user.primaryPositionTitle || '',
      secondaryPosition: user.secondaryPositionTitle || '',
      tertiaryPosition: user.tertiaryPositionTitle || '',
      primaryRank: user.primaryRank || 'Initiate',
      secondaryRank: user.secondaryRank || 'Initiate',
      tertiaryRank: user.tertiaryRank || 'Initiate',
      gameUserName: user.gameUserName,
      discordName: user.discordName,
      mainInGameRole: user.mainInGameRole,
      secondaryInGameRoles: user.secondaryInGameRoles,
      flags: user.flags,
    });
    setIsModalOpen(true);
    setIsViewModalOpen(true);
    // Fetch points data if admin
    if (currentUser?.role === 'Admin') {
      fetchUserPoints(user.id);
    }
  };

  const handleFlagToggle = (flag: string) => {
    if (!selectedUser) return;
    const currentFlags = editState.flags ?? selectedUser.flags ?? [];
    setEditState({
      ...editState,
      flags: currentFlags.includes(flag)
        ? currentFlags.filter((f) => f !== flag)
        : [...currentFlags, flag],
    });
  };

  const handleSave = async () => {
    if (!selectedUser) return;

    const getPositionId = (positionTitle: string | null | undefined): number | null => {
      if (positionTitle === null || positionTitle === undefined || positionTitle.trim() === '') {
        return null;
      }
      const position = positions.find((p) => p.title === positionTitle);
      return position?.id ?? null;
    };

    const payload = {
      Id: selectedUser.id,
      Username: selectedUser.username,
      Email: selectedUser.email,
      LastLogin: selectedUser.lastLogin,
      GameUserName: editState.gameUserName ?? selectedUser.gameUserName ?? '',
      DiscordName: editState.discordName ?? selectedUser.discordName ?? '',
      PrimaryGameRole: editState.mainInGameRole ?? selectedUser.mainInGameRole ?? '',
      SecondaryGameRole: editState.secondaryInGameRoles ?? selectedUser.secondaryInGameRoles ?? '',
      TertiaryGameRole: '',
      IsOnline: selectedUser.isOnline ?? false,
      ActivityScore: selectedUser.activityScore ?? 0,
      JoinDate:
        selectedUser.joinDate && !isNaN(Date.parse(selectedUser.joinDate))
          ? new Date(selectedUser.joinDate).toISOString()
          : new Date().toISOString(),
      PrimaryPositionId: getPositionId(editState.primaryPosition),
      SecondaryPositionId: getPositionId(editState.secondaryPosition),
      TertiaryPositionId: getPositionId(editState.tertiaryPosition),
      PrimaryRank: editState.primaryRank ?? 'Initiate',
      SecondaryRank: editState.secondaryRank ?? 'Initiate',
      TertiaryRank: editState.tertiaryRank ?? 'Initiate',
      Role: editState.role ?? selectedUser.role ?? 'Member',
      Flags: editState.flags ?? selectedUser?.flags ?? [],
    };

    try {
      const response = await axios.put(`/auth/user/${selectedUser.id}`, payload);
      const updatedUser = response.data;

      setUsers((prev) =>
        prev.map((u) =>
          u.id === updatedUser.id
            ? {
                ...u,
                username: updatedUser.username,
                email: updatedUser.email,
                gameUserName: updatedUser.gameUserName,
                discordName: updatedUser.discordName,
                role: updatedUser.role,
                avatarUrl: updatedUser.avatarUrl ?? null,
                mainInGameRole: updatedUser.primaryGameRole ?? '',
                secondaryInGameRoles: updatedUser.secondaryGameRole ?? '',
                activityScore: updatedUser.activityScore ?? 0,
                isOnline: updatedUser.isOnline ?? false,
                joinDate: updatedUser.joinDate ?? new Date().toISOString(),
                createdAt: updatedUser.createdAt ?? new Date().toISOString(),
                flags: updatedUser.flags ?? [],
                orgJourneyData: updatedUser.orgJourneyData ?? null,
                position: updatedUser.primaryPositionTitle ?? null,
                primaryPositionTitle: updatedUser.primaryPositionTitle ?? null,
                secondaryPositionTitle: updatedUser.secondaryPositionTitle ?? null,
                tertiaryPositionTitle: updatedUser.tertiaryPositionTitle ?? null,
              }
            : u
        )
      );

      setIsModalOpen(false);
      setIsViewModalOpen(false);
    } catch (err: any) {
      console.error('Failed to update user:', err);
    }
  };

  const fetchUserPoints = async (userId: number) => {
    try {
      const [balanceRes, historyRes] = await Promise.all([
        axios.get(`/auth/user/${userId}/points/balance`),
        axios.get(`/auth/user/${userId}/points/history`)
      ]);
      setUserPointsBalance(balanceRes.data.totalPoints || 0);
      setPointsHistory(historyRes.data || []);
    } catch (err: any) {
      console.error('Failed to fetch user points:', err);
    }
  };

  const handleAdjustPoints = async () => {
    if (!selectedUser) return;
    if (pointsAdjustment === 0) {
      alert('Please enter a points adjustment amount.');
      return;
    }
    if (!pointsReason.trim()) {
      alert('Please provide a reason for the adjustment.');
      return;
    }

    try {
      const res = await axios.post(`/auth/user/${selectedUser.id}/points`, {
        points: pointsAdjustment,
        reason: pointsReason
      });
      
      alert(`Points adjusted successfully! New total: ${res.data.newTotal}`);
      setPointsAdjustment(0);
      setPointsReason('');
      // Refresh points data
      await fetchUserPoints(selectedUser.id);
    } catch (err: any) {
      console.error('Failed to adjust points:', err);
      alert('Failed to adjust points. ' + (err.response?.data?.message || 'See console for details.'));
    }
  };

  const getFilteredUsers = () => {
    return users.filter((user) => {
      const matchesSearch =
        searchTerm === '' ||
        user.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.email.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesRole = filterRole === 'all' || user.role === filterRole;

      const matchesDepartment =
        filterDepartment === 'all' ||
        (() => {
          const positionTitles = [
            user.primaryPositionTitle,
            user.secondaryPositionTitle,
            user.tertiaryPositionTitle,
          ].filter(Boolean);

          return positionTitles.some((title) => {
            const position = positions.find((p) => p.title === title);
            return position?.department?.toLowerCase() === filterDepartment.toLowerCase();
          });
        })();

      const matchesOnlineStatus =
        filterOnlineStatus === 'all' ||
        (filterOnlineStatus === 'online' && user.isOnline) ||
        (filterOnlineStatus === 'offline' && !user.isOnline);

      const matchesPosition =
        filterPosition === 'all' ||
        user.primaryPositionTitle === filterPosition ||
        user.secondaryPositionTitle === filterPosition ||
        user.tertiaryPositionTitle === filterPosition;

      const matchesFlag = filterFlag === 'all' || (user.flags && user.flags.includes(filterFlag));

      const matchesLevel = filterLevel === 'all' || (() => {
        const points = userPoints.get(user.id) || 0;
        const level = getLevel(points).level;
        return level === parseInt(filterLevel);
      })();

      return (
        matchesSearch &&
        matchesRole &&
        matchesDepartment &&
        matchesOnlineStatus &&
        matchesPosition &&
        matchesFlag &&
        matchesLevel
      );
    });
  };

  const allPositions = new Set<string>();
  users.forEach((user) => {
    if (user.primaryPositionTitle) allPositions.add(user.primaryPositionTitle);
    if (user.secondaryPositionTitle) allPositions.add(user.secondaryPositionTitle);
    if (user.tertiaryPositionTitle) allPositions.add(user.tertiaryPositionTitle);
  });
  const uniquePositions = Array.from(allPositions).sort();

  const allFlags = new Set<string>();
  users.forEach((user) => {
    if (user.flags && Array.isArray(user.flags)) {
      user.flags.forEach((flag) => allFlags.add(flag));
    }
  });
  const uniqueFlags = Array.from(allFlags).sort();

  const renderEditModal = () => {
    if (!selectedUser) return null;

    // Debug: log user data to see what ranks are available
    console.log('Selected User Data:', {
      username: selectedUser.username,
      primaryRank: selectedUser.primaryRank,
      secondaryRank: selectedUser.secondaryRank,
      tertiaryRank: selectedUser.tertiaryRank
    });

    return (
      <div className={styles.modalOverlay} onClick={() => setIsModalOpen(false)}>
        <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
          <h3>Edit User: {selectedUser.username}</h3>
          {!canEditUser(selectedUser) && currentUser?.role === 'Leader' && (
            <div style={{ padding: '10px', marginBottom: '15px', backgroundColor: '#fff3cd', border: '1px solid #ffc107', borderRadius: '4px', color: '#856404' }}>
              ⚠️ You cannot edit Admins or other Leaders.
            </div>
          )}

          <div className={styles.fieldGroup}>
            <div className={styles.modalField}>
              <label>Role</label>
              <select
                value={editState.role ?? selectedUser.role}
                onChange={(e) => setEditState({ ...editState, role: e.target.value })}
                disabled={!canEditUser(selectedUser)}
              >
                <option value="Prospect">Prospect</option>
                <option value="Member">Member</option>
                <option value="Leader">Leader</option>
                {currentUser?.role === 'Admin' && <option value="Admin">Admin</option>}
              </select>
            </div>

            <div className={styles.modalField}>
              <label>Primary Position</label>
              <select
                value={editState.primaryPosition ?? selectedUser.primaryPositionTitle ?? ''}
                onChange={(e) => setEditState({ ...editState, primaryPosition: e.target.value })}
                disabled={!userScopes.includes('manageMembers')}
              >
                <option value="">None</option>
                {positions.map((pos) => (
                  <option key={pos.id} value={pos.title}>
                    {pos.title}
                  </option>
                ))}
              </select>
              <div className={styles.preferenceFooter}>
                User's Preference: <strong>{selectedUser.primaryRank || 'Not Set'}</strong>
              </div>
            </div>

            <div className={styles.modalField}>
              <label>Secondary Position</label>
              <select
                value={editState.secondaryPosition ?? selectedUser.secondaryPositionTitle ?? ''}
                onChange={(e) => setEditState({ ...editState, secondaryPosition: e.target.value })}
                disabled={!userScopes.includes('manageMembers')}
              >
                <option value="">None</option>
                {positions.map((pos) => (
                  <option key={pos.id} value={pos.title}>
                    {pos.title}
                  </option>
                ))}
              </select>
              <div className={styles.preferenceFooter}>
                User's Preference: <strong>{selectedUser.secondaryRank || 'Not Set'}</strong>
              </div>
            </div>

            <div className={styles.modalField}>
              <label>Tertiary Position</label>
              <select
                value={editState.tertiaryPosition ?? selectedUser.tertiaryPositionTitle ?? ''}
                onChange={(e) => setEditState({ ...editState, tertiaryPosition: e.target.value })}
                disabled={!userScopes.includes('manageMembers')}
              >
                <option value="">None</option>
                {positions.map((pos) => (
                  <option key={pos.id} value={pos.title}>
                    {pos.title}
                  </option>
                ))}
              </select>
              <div className={styles.preferenceFooter}>
                User's Preference: <strong>{selectedUser.tertiaryRank || 'Not Set'}</strong>
              </div>
            </div>

            <div className={styles.modalField}>
              <label>Game Username</label>
              <input
                type="text"
                value={editState.gameUserName ?? selectedUser.gameUserName ?? ''}
                onChange={(e) => setEditState({ ...editState, gameUserName: e.target.value })}
                disabled={!userScopes.includes('manageMembers')}
              />
            </div>

            <div className={styles.modalField}>
              <label>Discord Name</label>
              <input
                type="text"
                value={editState.discordName ?? selectedUser.discordName ?? ''}
                onChange={(e) => setEditState({ ...editState, discordName: e.target.value })}
                disabled={!userScopes.includes('manageMembers')}
              />
            </div>
          </div>

          <div className={styles.flagsSection}>
            <h4>Flags</h4>
            <div>
              {userMetadata.flags.map((flag) => (
                <div key={flag} className={styles.flagItem}>
                  <label className={styles.switchLabel}>
                    <label className={styles.switch}>
                      <input
                        type="checkbox"
                        checked={(editState.flags ?? selectedUser?.flags ?? []).includes(flag)}
                        onChange={() => handleFlagToggle(flag)}
                      />
                      <span className={styles.slider}></span>
                    </label>
                    {formatFlag(flag)}
                  </label>
                </div>
              ))}
            </div>
          </div>

          {currentUser?.role === 'Admin' && (
            <div className={styles.pointsSection}>
              <div className={styles.pointsHeader}>
                <div>
                  <h4>ORG Points Management</h4>
                  <p className={styles.sectionDescription}>Add or remove points from this user's account</p>
                </div>
                <div className={styles.currentBalance}>
                  <span className={styles.balanceLabel}>Current Balance:</span>
                  <span className={styles.balanceAmount}>{userPointsBalance.toLocaleString()}</span>
                  <span className={styles.pointsUnit}>pts</span>
                </div>
              </div>
              
              <div className={styles.pointsTabs}>
                <button 
                  className={!showPointsHistory ? styles.tabActive : styles.tab}
                  onClick={() => setShowPointsHistory(false)}
                >
                  Adjust Points
                </button>
                <button 
                  className={showPointsHistory ? styles.tabActive : styles.tab}
                  onClick={() => setShowPointsHistory(true)}
                >
                  Points History
                </button>
              </div>

              {!showPointsHistory ? (
                <div className={styles.pointsControls}>
                  <div className={styles.modalField}>
                    <label>Points Adjustment</label>
                    <input
                      type="number"
                      value={pointsAdjustment}
                      onChange={(e) => setPointsAdjustment(parseInt(e.target.value) || 0)}
                      placeholder="Enter amount (+ to add, - to remove)"
                    />
                    <small>Use positive numbers to add points, negative to remove</small>
                  </div>
                  <div className={styles.modalField}>
                    <label>Reason</label>
                    <input
                      type="text"
                      value={pointsReason}
                      onChange={(e) => setPointsReason(e.target.value)}
                      placeholder="e.g., Event participation, penalty, etc."
                    />
                  </div>
                  <button
                    className={styles.buttonPrimary}
                    onClick={handleAdjustPoints}
                    disabled={pointsAdjustment === 0 || !pointsReason.trim()}
                  >
                    Adjust Points
                  </button>
                </div>
              ) : (
                <div className={styles.pointsHistory}>
                  {pointsHistory.length === 0 ? (
                    <p className={styles.noHistory}>No points history available</p>
                  ) : (
                    <div className={styles.historyList}>
                      {pointsHistory.map((transaction) => (
                        <div key={transaction.id} className={styles.historyItem}>
                          <div className={styles.historyInfo}>
                            <div className={styles.historyReason}>
                              {transaction.missionTitle ? (
                                <>
                                  <strong>Mission Reward:</strong> {transaction.missionTitle}
                                </>
                              ) : (
                                transaction.reason
                              )}
                            </div>
                            <div className={styles.historyDate}>
                              {new Date(transaction.createdAt).toLocaleString()}
                              {transaction.createdBy && (
                                <span className={styles.createdBy}> • By: {transaction.createdBy}</span>
                              )}
                            </div>
                          </div>
                          <div className={transaction.points >= 0 ? styles.pointsPositive : styles.pointsNegative}>
                            {transaction.points >= 0 ? '+' : ''}{transaction.points}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          <div className={styles.modalButtons}>
            <button
              className="button"
              onClick={handleSave}
              disabled={!canEditUser(selectedUser)}
            >
              Save
            </button>

            <button className="button outline" onClick={() => setIsModalOpen(false)}>
              Cancel
            </button>

            {userScopes.includes('manageMembers') && (
              <button
                className={styles.dangerButton}
                onClick={async () => {
                  if (
                    window.confirm(
                      `Are you sure you want to delete ${selectedUser.username}'s account? This cannot be undone.`
                    )
                  ) {
                    try {
                      await axios.delete(`/auth/user/${selectedUser.id}`);
                      alert('User deleted.');
                      setIsModalOpen(false);
                      setIsViewModalOpen(false);
                      fetchUsers();
                    } catch (err) {
                      console.error('Failed to delete user:', err);
                      alert('Failed to delete user. See console for details.');
                    }
                  }
                }}
              >
                🗑️ Delete Account
              </button>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className={styles.leaderDashboardContainer}>
      <div className={styles.card}>
        <div className={styles.headerRow}>
          <h2 className={styles.title}>Manage Members</h2>

          <div className={styles.viewToggle}>
            <button
              className={`${styles.viewToggleBtn} ${viewMode === 'table' ? styles.active : ''}`}
              onClick={() => setViewMode('table')}
            >
              List View
            </button>
            <button
              className={`${styles.viewToggleBtn} ${viewMode === 'card' ? styles.active : ''}`}
              onClick={() => setViewMode('card')}
            >
              Card View
            </button>
          </div>
        </div>

        {/* Filter Controls */}
        <div className={styles.filterControls}>
          <input
            type="text"
            placeholder="Search by username or email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className={styles.searchInput}
          />

          <select
            value={filterRole}
            onChange={(e) => setFilterRole(e.target.value)}
            className={styles.filterSelect}
          >
            <option value="all">All Roles</option>
            <option value="Prospect">Prospect</option>
            <option value="Member">Member</option>
            <option value="Leader">Leader</option>
            <option value="Admin">Admin</option>
          </select>

          <select
            value={filterDepartment}
            onChange={(e) => setFilterDepartment(e.target.value)}
            className={styles.filterSelect}
          >
            <option value="all">All Departments</option>
            <option value="Engineering">Engineering</option>
            <option value="Medical">Medical</option>
            <option value="Security">Security</option>
            <option value="Logistics">Logistics</option>
            <option value="Science">Science</option>
            <option value="Operations">Operations</option>
          </select>

          <select
            value={filterPosition}
            onChange={(e) => setFilterPosition(e.target.value)}
            className={styles.filterSelect}
          >
            <option value="all">All Positions</option>
            {uniquePositions.map((position) => (
              <option key={position} value={position}>
                {position}
              </option>
            ))}
          </select>

          <select
            value={filterFlag}
            onChange={(e) => setFilterFlag(e.target.value)}
            className={styles.filterSelect}
          >
            <option value="all">All Flags</option>
            {uniqueFlags.map((flag) => (
              <option key={flag} value={flag}>
                {formatFlag(flag)}
              </option>
            ))}
          </select>

          <select
            value={filterOnlineStatus}
            onChange={(e) => setFilterOnlineStatus(e.target.value)}
            className={styles.filterSelect}
          >
            <option value="all">All Status</option>
            <option value="online">Online</option>
            <option value="offline">Offline</option>
          </select>

          <select
            value={filterLevel}
            onChange={(e) => setFilterLevel(e.target.value)}
            className={styles.filterSelect}
          >
            <option value="all">All Levels</option>
            <option value="0">Level 0 - Applicant</option>
            <option value="1">Level 1 - Junior Contributor</option>
            <option value="2">Level 2 - Contributor</option>
            <option value="3">Level 3 - Skilled Contributor</option>
            <option value="4">Level 4 - Senior Contributor</option>
            <option value="5">Level 5 - Distinguished Contributor</option>
            <option value="6">Level 6 - Commander</option>
            <option value="7">Level 7 - Champion</option>
            <option value="8">Level 8 - Legend</option>
            <option value="9">Level 9 - Hero</option>
            <option value="6">Level 6 - Elite Contributor</option>
            <option value="7">Level 7 - Premier Contributor</option>
            <option value="8">Level 8 - Vanguard Contributor</option>
            <option value="9">Level 9 - Luminary Contributor</option>
            <option value="10">Level 10 - Legacy Member</option>
          </select>

          <button
            onClick={() => {
              setSearchTerm('');
              setFilterRole('all');
              setFilterDepartment('all');
              setFilterPosition('all');
              setFilterFlag('all');
              setFilterOnlineStatus('all');
              setFilterLevel('all');
            }}
            className={`button ${styles.clearFiltersBtn}`}
          >
            Clear Filters
          </button>
        </div>

        <div className={styles.filterResults}>
          Showing {getFilteredUsers().length} of {users.length} members
        </div>

        {/* Table View */}
        {viewMode === 'table' && (
          <table className={styles.userTable}>
            <thead>
              <tr>
                <th>Username</th>
                <th>Email</th>
                <th>Role</th>
                <th>Position</th>
                <th>Points / Level</th>
                <th>Join Date</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {getFilteredUsers().map((user) => (
                <tr key={user.id}>
                  <td className={styles.userInfoCell}>
                    <img
                      src={
                        getFullAvatarUrl(user.avatarUrl) ||
                        'https://storage.tficorg.org/DefaultIcon.png'
                      }
                      alt="Avatar"
                      className={`${styles.avatarThumb} ${
                        user.isOnline ? styles.onlineAvatar : styles.offlineAvatar
                      }`}
                      style={{ borderColor: getAvatarBorderColor(user.profileTheme) }}
                    />
                    <span>{user.username}</span>
                  </td>
                  <td>{user.email}</td>
                  <td>{user.role}</td>
                  <td>
                    {[user?.primaryPositionTitle, user?.secondaryPositionTitle, user?.tertiaryPositionTitle]
                      .filter(Boolean)
                      .join(', ') || 'None'}
                  </td>
                  <td>
                    {(() => {
                      const points = userPoints.get(user.id) || 0;
                      const { level } = getLevel(points);
                      return (
                        <div className={styles.pointsLevelCell}>
                          <span className={styles.pointsValue}>{points.toLocaleString()} pts</span>
                          <span className={styles.levelBadge}>Level {level}</span>
                          {user.commendationsCount !== undefined && user.commendationsCount > 0 && (
                            <span className={styles.commendationsBadge}>
                              🏅 {user.commendationsCount}
                            </span>
                          )}
                        </div>
                      );
                    })()}
                  </td>
                  <td>{new Date(user.joinDate).toLocaleDateString()}</td>
                  <td>
                    <button className="button small" onClick={() => handleUserClick(user)}>
                      View
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {/* Card View */}
        {viewMode === 'card' && (
          <div className={styles.memberCardGrid}>
            {getFilteredUsers().map((user) => (
              <div key={user.id} className={styles.memberCard}>
                <div className={styles.memberCardHeader}>
                  <img
                    src={
                      getFullAvatarUrl(user.avatarUrl) ||
                      'https://storage.tficorg.org/DefaultIcon.png'
                    }
                    alt="Avatar"
                    className={`${styles.memberCardAvatar} ${
                      user.isOnline ? styles.onlineAvatar : styles.offlineAvatar
                    }`}
                    style={{ borderColor: getAvatarBorderColor(user.profileTheme) }}
                  />
                  <div className={styles.memberCardInfo}>
                    <h3 className={styles.memberCardName}>{user.username}</h3>
                    <span className={styles.memberCardRole}>{user.role}</span>
                  </div>
                </div>

                <div className={styles.memberCardBody}>
                  <div className={styles.memberCardField}>
                    <strong>Email:</strong> {user.email}
                  </div>
                  <div className={styles.memberCardField}>
                    <strong>Position:</strong>{' '}
                    {[user?.primaryPositionTitle, user?.secondaryPositionTitle, user?.tertiaryPositionTitle]
                      .filter(Boolean)
                      .join(', ') || 'None'}
                  </div>
                  <div className={styles.memberCardField}>
                    <strong>Join Date:</strong> {new Date(user.joinDate).toLocaleDateString()}
                  </div>
                  <div className={styles.memberCardField}>
                    <strong>Points / Level:</strong>
                    <div className={styles.pointsLevelDisplay}>
                      {(() => {
                        const points = userPoints.get(user.id) || 0;
                        const { level } = getLevel(points);
                        return (
                          <>
                            <span className={styles.pointsValue}>{points.toLocaleString()} pts</span>
                            <span className={styles.levelBadge}>Level {level}</span>
                            {user.commendationsCount !== undefined && user.commendationsCount > 0 && (
                              <span className={styles.commendationsBadge}>
                                🏅 {user.commendationsCount}
                              </span>
                            )}
                          </>
                        );
                      })()}
                    </div>
                  </div>
                  {user.flags && user.flags.length > 0 && (
                    <div className={styles.memberCardField}>
                      <strong>Flags:</strong>
                      <div className={styles.memberCardFlags}>
                        {user.flags.map((flag, idx) => (
                          <span key={idx} className={styles.flagBadge}>
                            {formatFlag(flag)}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                <div className={styles.memberCardFooter}>
                  <button className="button small" onClick={() => handleUserClick(user)}>
                    View Details
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {isModalOpen && renderEditModal()}
      </div>
    </div>
  );
};

export default UserManagement;
